import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, Send, Copy, Wallet as WalletIcon, ShieldAlert } from "lucide-react";
import { businessApi, aiApi } from "../../lib/ledgerApi";
import { useAuth } from "../../contexts/AuthContext";
import HashString from "../../components/HashString";
import InsightsCard from "../../components/InsightsCard";
import ContractsCard from "../../components/ContractsCard";
import AgentActivityCard from "../../components/AgentActivityCard";
import { toast } from "sonner";

const fmtNum = (s) => {
  try {
    const n = Number(s);
    if (Number.isFinite(n))
      return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
  } catch {}
  return s;
};

export default function AppDashboard() {
  const { business } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [txns, setTxns] = useState([]);
  const [holdsCount, setHoldsCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [w, t, h] = await Promise.all([
          businessApi.wallets(),
          businessApi.transactions({ limit: 10 }),
          aiApi.myHolds().catch(() => []),
        ]);
        if (alive) {
          setWallets(w);
          setTxns(t);
          setHoldsCount((h || []).filter((x) => x.status === "pending").length);
        }
      } catch {}
    };
    load();
    const i = setInterval(load, 4000);
    return () => {
      alive = false;
      clearInterval(i);
    };
  }, []);

  const myAddresses = new Set(wallets.map((w) => w.ledger_address));

  return (
    <div className="max-w-6xl px-6 md:px-10 py-10 md:py-14">
      <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Dashboard</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium">
            {business?.legal_name || "Welcome"}
          </h1>
          {business && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(business.full_vpa);
                toast.success("VPA copied");
              }}
              data-testid="dashboard-vpa"
              className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-brand/40 bg-brand/10 text-brand text-xs font-mono"
            >
              {business.full_vpa} <Copy className="w-3 h-3" strokeWidth={1.5} />
            </button>
          )}
        </div>
        <Link
          to="/app/send"
          data-testid="dashboard-quick-send"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-foreground bg-foreground text-background text-sm hover:opacity-90"
        >
          <Send className="w-4 h-4" strokeWidth={1.5} /> Send
        </Link>
      </div>

      {holdsCount > 0 && (
        <Link
          to="/app/holds"
          data-testid="dashboard-holds-banner"
          className="mb-6 flex items-center gap-3 px-4 py-3 border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded-sm hover:border-amber-500 transition-colors"
        >
          <ShieldAlert className="w-4 h-4" strokeWidth={1.5} />
          <div className="text-sm">
            <span className="font-medium">{holdsCount}</span> transfer{holdsCount === 1 ? "" : "s"} held for review
          </div>
          <span className="ml-auto text-xs uppercase tracking-widest">Open queue →</span>
        </Link>
      )}

      {/* Wallet summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {wallets.map((w) => (
          <Link
            key={w.currency}
            to="/app/wallets"
            data-testid={`dash-wallet-${w.currency}`}
            className="p-4 border border-border bg-card rounded-sm hover:border-foreground transition-colors"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{w.currency}</div>
            <div className="font-mono text-xl">{fmtNum(w.balance)}</div>
          </Link>
        ))}
      </div>

      {/* Recent transactions + AI insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-12">
        <div className="lg:col-span-2">
          <h2 className="text-lg font-medium mb-3">Recent transactions</h2>
          <div className="border border-border rounded-sm bg-card divide-y divide-border">
        {txns.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">No transactions yet. <Link to="/app/send" className="underline">Send your first one →</Link></div>
        )}
        {txns.map((tx) => {
          const outgoing = myAddresses.has(tx.from_address);
          return (
          <Link to={`/app/tx/${tx.tx_id}`} key={tx.tx_id} data-testid={`dash-tx-${tx.tx_id}`} className="grid grid-cols-12 gap-3 p-4 items-center hover:bg-muted/40 transition-colors">
              <div className="col-span-1 hidden sm:flex justify-center">
                {outgoing ? (
                  <ArrowUpRight className="w-4 h-4 text-status-tampered" strokeWidth={1.5} />
                ) : (
                  <ArrowDownLeft className="w-4 h-4 text-status-ok" strokeWidth={1.5} />
                )}
              </div>
              <div className="col-span-12 sm:col-span-5 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1 flex items-center gap-2 flex-wrap">
                  <span>{tx.kind === "coinbase" ? "Treasury mint" : outgoing ? "Sent" : "Received"} · {tx.status}</span>
                  {tx.bundle_id && (
                    <span className="px-1.5 py-0.5 border border-status-info/40 bg-status-info/10 text-status-info rounded-sm font-mono normal-case tracking-normal">
                      FX · {tx.bundle_role || "leg"}
                    </span>
                  )}
                </div>
                <HashString value={tx.tx_id} label="Tx id" />
                {tx.memo && <div className="text-[11px] text-muted-foreground mt-1 truncate">{tx.memo}</div>}
              </div>
              <div className="col-span-7 sm:col-span-3 min-w-0 text-xs font-mono text-muted-foreground">
                <div className="truncate">
                  {outgoing ? "to " : "from "}
                  <span className="text-foreground">{outgoing ? tx.to_address.slice(0, 16) + "…" : tx.from_address.slice(0, 16) + "…"}</span>
                </div>
                <div className="text-[10px]">{new Date(tx.timestamp).toLocaleString()}</div>
              </div>
              <div className="col-span-5 sm:col-span-3 text-right font-mono">
                <span className={outgoing ? "text-status-tampered" : "text-status-ok"}>
                  {outgoing ? "−" : "+"}{fmtNum(tx.amount)}
                </span>{" "}
                <span className="text-muted-foreground">{tx.currency}</span>
              </div>
            </Link>
          );
        })}
          </div>
        </div>
        <div className="lg:col-span-1 space-y-6">
          <ContractsCard />
          <AgentActivityCard />
          <InsightsCard />
        </div>
      </div>
    </div>
  );
}
